import { useMemo } from 'react';
import { ITaskItemAttributes, ITaskTableRow, TASK_SORT_OPTIONS, TASK_STATUS_CONFIG, TaskViewType, TASK_VIEW_TYPES } from '@/types/admin/tasks';

const orderBy = <T,>(array: T[], keys: (keyof T)[], orders: ('asc' | 'desc')[]): T[] => {
  return [...array].sort((a, b) => {
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const order = orders[i] || 'asc';
      let aVal: any = (a as any)[key];
      let bVal: any = (b as any)[key];
      if (aVal == null && bVal == null) continue;
      if (aVal == null) return order === 'asc' ? 1 : -1;
      if (bVal == null) return order === 'asc' ? -1 : 1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        if (/^\d{4}-\d{2}-\d{2}/.test(aVal) && /^\d{4}-\d{2}-\d{2}/.test(bVal)) {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
      }
      let comparison = 0;
      if (aVal < bVal) comparison = -1;
      else if (aVal > bVal) comparison = 1;
      if (comparison !== 0) return order === 'asc' ? comparison : -comparison;
    }
    return 0;
  });
};

export const useVisibleTasks = (
  data: { data?: ITaskItemAttributes[] } | undefined,
  view: TaskViewType,
  searchQuery: string,
  filterModel: any,
  sortModel: Array<{ field: string; sort: 'asc' | 'desc' }>,
) => {
  return useMemo(() => {
    const baseTasks = Array.isArray(data?.data) ? data!.data : [];
    let filteredTasks = view === TASK_VIEW_TYPES.LIST
      ? baseTasks.filter((task) => !task.isArchived)
      : baseTasks;

    const query = (searchQuery || '').trim().toLowerCase();
    if (query) {
      filteredTasks = filteredTasks.filter((task) => {
        const haystacks: string[] = [];
        if (task.title) haystacks.push(task.title);
        if (task.description) haystacks.push(task.description);
        if (task.location) haystacks.push(task.location);
        if (task.category) haystacks.push(task.category);
        task.assignees?.forEach((a) => {
          const fullName = `${a.firstName || ''} ${a.lastName || ''}`.trim();
          if (fullName) haystacks.push(fullName);
          if (a.email) haystacks.push(a.email);
        });
        haystacks.push(String(task.id));
        return haystacks.some((h) => h.toLowerCase().includes(query));
      });
    }

    if ((filterModel.assignedTo?.length || 0) > 0 || (filterModel.assignedToNot?.length || 0) > 0) {
      filteredTasks = filteredTasks.filter((task) => {
        const names = (task.assignees || []).map((a) => `${a.firstName || ''} ${a.lastName || ''}`.trim());
        const isUnassigned = !task.assignees || task.assignees.length === 0;
        let includePass = true;
        if (filterModel.assignedTo?.length) {
          const wantsUnassigned = filterModel.assignedTo.includes('unassigned');
          includePass = wantsUnassigned ? isUnassigned || names.some((n) => filterModel.assignedTo!.includes(n)) : names.some((n) => filterModel.assignedTo!.includes(n));
        }
        let excludePass = true;
        if (filterModel.assignedToNot?.length) {
          const excludesUnassigned = filterModel.assignedToNot.includes('unassigned');
          const excludedName = names.some((n) => filterModel.assignedToNot!.includes(n));
          excludePass = excludesUnassigned ? !isUnassigned && !excludedName : !excludedName;
        }
        return includePass && excludePass;
      });
    }

    if ((filterModel.creator?.length || 0) > 0 || (filterModel.creatorNot?.length || 0) > 0) {
      filteredTasks = filteredTasks.filter((task) => {
        const isResident = task.isResidentRequest === true;
        let includePass = true;
        if (filterModel.creator?.length) {
          includePass = filterModel.creator.some((creator: string) => (creator === 'resident' ? isResident : !isResident));
        }
        let excludePass = true;
        if (filterModel.creatorNot?.length) {
          excludePass = !filterModel.creatorNot.some((creator: string) => (creator === 'resident' ? isResident : !isResident));
        }
        return includePass && excludePass;
      });
    }

    if (view === TASK_VIEW_TYPES.LIST && ((filterModel.status?.length || 0) > 0 || (filterModel.statusNot?.length || 0) > 0)) {
      filteredTasks = filteredTasks.filter((task) => {
        let includePass = true;
        if (filterModel.status?.length) includePass = filterModel.status.includes(task.taskStatus as any);
        let excludePass = true;
        if (filterModel.statusNot?.length) excludePass = !filterModel.statusNot.includes(task.taskStatus as any);
        return includePass && excludePass;
      });
    }

    if (sortModel?.length) {
      const sortConfig = TASK_SORT_OPTIONS.find((opt) => opt.value === sortModel[0].field) || TASK_SORT_OPTIONS[0];
      const userSortDirection = sortModel[0].sort;
      const naturalDirection = sortConfig.order[0];
      const shouldReverse = userSortDirection !== naturalDirection;
      const finalSortOrder = (shouldReverse
        ? [...sortConfig.order].map((o) => (o === 'asc' ? 'desc' : 'asc'))
        : [...sortConfig.order]) as Array<'asc' | 'desc'>;
      filteredTasks = orderBy(
        filteredTasks,
        [...sortConfig.keys] as Array<keyof ITaskItemAttributes>,
        finalSortOrder,
      );
    }

    return filteredTasks;
  }, [data, view, searchQuery, filterModel, sortModel]);
};

export const useGroupedTasks = (visibleTasks: ITaskItemAttributes[], view: TaskViewType) => {
  return useMemo(() => {
    if (!Array.isArray(visibleTasks) || view === TASK_VIEW_TYPES.ARCHIVED) {
      const emptyGroups: Record<string, ITaskItemAttributes[]> = {};
      Object.keys(TASK_STATUS_CONFIG).forEach((status) => (emptyGroups[status] = []));
      return emptyGroups;
    }
    const groups: Record<string, ITaskItemAttributes[]> = {};
    Object.keys(TASK_STATUS_CONFIG).forEach((status) => (groups[status] = []));
    visibleTasks.forEach((task) => { if (groups[task.taskStatus]) groups[task.taskStatus].push(task); });
    return groups;
  }, [visibleTasks, view]);
};

export const useTableRows = (visibleTasks: ITaskItemAttributes[]) => {
  return useMemo<ITaskTableRow[]>(() => {
    if (!visibleTasks) return [];
    return visibleTasks.map((task) => ({
      id: task.id,
      title: task.title,
      taskStatus: task.taskStatus,
      priority: task.priority,
      category: task.category || 'unassigned',
      assignees: task.assignees,
      dueDate: task.dueDate,
      isBlocked: task.isBlocked,
      subtasks: task.subtasks || [],
      files: task.files,
      isArchived: task.isArchived,
      createdAt: task.createdAt,
      description: task.description,
      location: task.location,
    }));
  }, [visibleTasks]);
};

export const useUniqueAssignees = (data: { data?: ITaskItemAttributes[] } | undefined, view: TaskViewType) => {
  return useMemo(() => {
    const baseTasks = Array.isArray(data?.data) ? (data!.data as ITaskItemAttributes[]) : [];
    const assigneeNames = new Set<string>();
    baseTasks.forEach((task) => {
      if (view === TASK_VIEW_TYPES.ARCHIVED ? !task.isArchived : task.isArchived) return;
      task.assignees?.forEach((a) => {
        const fullName = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        if (fullName) assigneeNames.add(fullName);
      });
    });
    return Array.from(assigneeNames);
  }, [data, view]);
};


